import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { TarjetaEmpresa } from './carga-empresas/interface/tarjeta-empresa.interface';
import { ReporteGuardado } from 'src/interfaces/reporte-guardado.interface';
import { NovedadGuardada } from 'src/interfaces/novedad-guardada.interface';

@Injectable({
    providedIn: 'root',
})
export class RegistroService {
    private readonly apiUrl = environment.apiSiriUrl;
    constructor(private readonly http: HttpClient) {}

    // Subjects para booleanos
    private readonly _registradoSubject = new BehaviorSubject<boolean>(
        this.getBooleanFromLocalStorage('registrado')
    );
    private readonly _empresasAsociadasSubject = new BehaviorSubject<boolean>(
        this.getBooleanFromLocalStorage('empresasAsociadas')
    );

    // Subject para strings
    private readonly _empresaSelectedSubject = new BehaviorSubject<
        string | null
    >(this.getStringFromLocalStorage('idEmpresaSeleccionada'));
    private readonly _rolUsuarioSubject = new BehaviorSubject<string | null>(
        null
    );
    private readonly _correoCorporativoSubject = new BehaviorSubject<
        string | null
    >(this.getStringFromLocalStorage('correoCorporativoSubject'));

    private readonly _nitEmpresaSelectedSubject = new BehaviorSubject<
        string | null
    >(this.getStringFromLocalStorage('nitEmpresaSeleccionada'));
    private readonly _empresaSelectedEslabonSubject = new BehaviorSubject<
        string | null
    >(this.getStringFromLocalStorage('empresaSeleccionadaEslabon'));

    private readonly _caracterizacionComercializadorSubject =
        new BehaviorSubject<unknown | null>(
            this.getJsonFromLocalStorage('caracterizacionComercializador')
        );

    private readonly _permisosSubject = new BehaviorSubject<unknown | null>(
        null
    );

    private readonly _representanteAgenteVigiladoSubject =
        new BehaviorSubject<unknown | null>(
            this.getJsonFromLocalStorage('representanteAgenteVigilado')
        );

    private readonly _listaEmpresasAsociadasSubject = new BehaviorSubject<
        TarjetaEmpresa[]
    >(this.getEmpresasFromLocalStorage());
    private readonly _listaReportesGuardadosSubject = new BehaviorSubject<
        ReporteGuardado[]
    >(this.getReportesGuardadosFromLocalStorage());

    private readonly _listaNovedadesGuardadasSubject = new BehaviorSubject<
        NovedadGuardada[]
    >(this.getNovedadesGuardadasFromLocalStorage());

    private readonly _usuarioKeycloakSubject =
        new BehaviorSubject<unknown | null>(null);

    // Observables
    registrado$ = this._registradoSubject.asObservable();
    empresasAsociadas$ = this._empresasAsociadasSubject.asObservable();
    empresaSelected$ = this._empresaSelectedSubject.asObservable();
    nitEmpresaSelected$ = this._nitEmpresaSelectedSubject.asObservable();
    rolUsuario$ = this._rolUsuarioSubject.asObservable();
    listaEmpresasAsociadas$ =
        this._listaEmpresasAsociadasSubject.asObservable();
    listaReportesGuardados$ =
        this._listaReportesGuardadosSubject.asObservable();
    listaNovedadesGuardadas$ =
        this._listaNovedadesGuardadasSubject.asObservable();
    correoCorporativo$ = this._correoCorporativoSubject.asObservable();
    eslabonEmpresaSelected$ =
        this._empresaSelectedEslabonSubject.asObservable();
    caracterizacionComercializador$ =
        this._caracterizacionComercializadorSubject.asObservable();
    permisos$ = this._permisosSubject.asObservable();
    representanteAgenteVigilado$ =
        this._representanteAgenteVigiladoSubject.asObservable();

    // Métodos para obtener el valor del localStorage

    private getBooleanFromLocalStorage(key: string): boolean {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : false;
    }

    private getStringFromLocalStorage(key: string): string | null {
        return localStorage.getItem(key);
    }

    private getJsonFromLocalStorage(key: string): unknown | null {
        const item = localStorage.getItem(key);
        if (!item) return null;
        try {
            return JSON.parse(item) as unknown;
        } catch {
            return null;
        }
    }

    // Getters y setters para booleanos
    get registrado(): boolean {
        return this._registradoSubject.value;
    }

    set registrado(value: boolean) {
        this._registradoSubject.next(value);
        localStorage.setItem('registrado', JSON.stringify(value));
    }

    get empresasAsociadas(): boolean {
        return this._empresasAsociadasSubject.value;
    }

    set empresasAsociadas(value: boolean) {
        this._empresasAsociadasSubject.next(value);
        localStorage.setItem('empresasAsociadas', JSON.stringify(value));
    }

    // Getters y setters para strings
    get empresaSelected(): string | null {
        return this._empresaSelectedSubject.value;
    }

    set empresaSelected(value: string | null) {
        const normalized =
            value === null || value === undefined ? null : String(value);
        this._empresaSelectedSubject.next(normalized);
        if (normalized) {
            localStorage.setItem('idEmpresaSeleccionada', normalized);
        } else {
            localStorage.removeItem('idEmpresaSeleccionada');
        }
    }

    get caracterizacionComercializador(): unknown | null {
        return this._caracterizacionComercializadorSubject.value;
    }

    set caracterizacionComercializador(value: unknown | null) {
        this._caracterizacionComercializadorSubject.next(value);
        if (value !== null && value !== undefined) {
            localStorage.setItem(
                'caracterizacionComercializador',
                JSON.stringify(value)
            );
        } else {
            localStorage.removeItem('caracterizacionComercializador');
        }
    }

    get permisos(): unknown | null {
        return this._permisosSubject.value;
    }

    set permisos(value: unknown | null) {
        this._permisosSubject.next(value);
    }

    get representanteAgenteVigilado(): unknown | null {
        return this._representanteAgenteVigiladoSubject.value;
    }

    set representanteAgenteVigilado(value: unknown | null) {
        this._representanteAgenteVigiladoSubject.next(value);
        if (value !== null && value !== undefined) {
            localStorage.setItem(
                'representanteAgenteVigilado',
                JSON.stringify(value)
            );
        } else {
            localStorage.removeItem('representanteAgenteVigilado');
        }
    }

    get rolUsuario(): string | null {
        return this._rolUsuarioSubject.value;
    }

    set rolUsuario(value: string | null) {
        this._rolUsuarioSubject.next(value);
    }

    get correoCorporativo(): string | null {
        return this._correoCorporativoSubject.value;
    }

    set correoCorporativo(value: string | null) {
        this._correoCorporativoSubject.next(value);
        if (value) {
            localStorage.setItem('correoCorporativoSubject', value);
        } else {
            localStorage.removeItem('correoCorporativoSubject');
        }
    }

    get nitEmpresaSelected(): string | null {
        return this._nitEmpresaSelectedSubject.value;
    }

    set nitEmpresaSelected(value: string | null) {
        this._nitEmpresaSelectedSubject.next(value);
        if (value) {
            localStorage.setItem('nitEmpresaSeleccionada', value);
        } else {
            localStorage.removeItem('nitEmpresaSeleccionada');
        }
    }

    private getEmpresasFromLocalStorage(): TarjetaEmpresa[] {
        const item = localStorage.getItem('listaEmpresasAsociadas');
        return item ? JSON.parse(item) : [];
    }

    get listaEmpresasAsociadas(): TarjetaEmpresa[] {
        return this._listaEmpresasAsociadasSubject.value;
    }

    set listaEmpresasAsociadas(value: TarjetaEmpresa[]) {
        this._listaEmpresasAsociadasSubject.next(value);
        localStorage.setItem('listaEmpresasAsociadas', JSON.stringify(value));
    }

    private getReportesGuardadosFromLocalStorage(): ReporteGuardado[] {
        const item = localStorage.getItem('listaReportesGuardados');
        return item ? JSON.parse(item) : [];
    }

    private getNovedadesGuardadasFromLocalStorage(): NovedadGuardada[] {
        const item = localStorage.getItem('listaNovedadesGuardados');
        return item ? JSON.parse(item) : [];
    }

    get listaReportesGuardados(): ReporteGuardado[] {
        return this._listaReportesGuardadosSubject.value;
    }

    get listaNovedadesGuardadas(): NovedadGuardada[] {
        return this._listaNovedadesGuardadasSubject.value;
    }

    set listaReportesGuardados(value: ReporteGuardado[]) {
        this._listaReportesGuardadosSubject.next(value);
        localStorage.setItem('listaReportesGuardados', JSON.stringify(value));
    }

    set listaNovedadesGuardadas(value: NovedadGuardada[]) {
        this._listaNovedadesGuardadasSubject.next(value);
        localStorage.setItem('listaNovedadesGuardadas', JSON.stringify(value));
    }

    get eslabonEmpresaSelected(): string | null {
        return this._empresaSelectedEslabonSubject.value;
    }

    set eslabonEmpresaSelected(value: string | null) {
        this._empresaSelectedEslabonSubject.next(value);
        if (value) {
            localStorage.setItem('empresaSeleccionadaEslabon', value);
        } else {
            localStorage.removeItem('empresaSeleccionadaEslabon');
        }
    }

    /**
     * Limpia todo el estado de sesión (BehaviorSubjects + localStorage).
     *
     * Debe invocarse al cerrar sesión para que, al volver a iniciar, no se
     * conserve la empresa seleccionada ni datos del usuario anterior.
     */
    limpiarEstadoSesion(): void {
        this.empresaSelected = null;
        this.nitEmpresaSelected = null;
        this.eslabonEmpresaSelected = null;
        this.correoCorporativo = null;
        this.representanteAgenteVigilado = null;
        this.caracterizacionComercializador = null;
        this.permisos = null;
        this.rolUsuario = null;
        this.usuarioKeycloak = null;
        this.registrado = false;
        this.empresasAsociadas = false;
        this.listaEmpresasAsociadas = [];
        this.listaReportesGuardados = [];
        this.listaNovedadesGuardadas = [];
        // Clave inconsistente de novedades (getter lee 'listaNovedadesGuardados').
        localStorage.removeItem('listaNovedadesGuardados');
    }

    verificarEmpresa(idEmpresa: number) {
        const url = `${this.apiUrl}agente-vigilado/${idEmpresa}/validar-datos`;
        return this.http.get<{
            requiereActualizacion: boolean;
            mensaje: string;
        }>(url);
    }

    get usuarioKeycloak(): unknown | null {
        return this._usuarioKeycloakSubject.value ?? null;
    }

    set usuarioKeycloak(value: unknown | null) {
        this._usuarioKeycloakSubject.next(value);
    }
}
